if (!customElements.get('swatch-manager')) {
  class SwatchManager extends HTMLElement {
    constructor() {
      super();
      this.desktopCount = parseInt(this.dataset.desktopCount || 0);
      this.mobileCount = parseInt(this.dataset.mobileCount || 0);
      this.mediumCount = 4; // Fixed count for medium screens (1024px - 1440px)

      // Keep original mobile query but update breakpoint
      this.mediaQuery = window.matchMedia('(max-width: 1100px)');
      // Add medium screen query
      this.mediumQuery = window.matchMedia('(min-width: 1100px) and (max-width: 1520px)');

      // Cache DOM elements
      this.swatchContainers = Array.from(this.querySelectorAll('.card__swatches'));

      // Bind the resize handler once
      this.resizeTimeout = null;
    }

    connectedCallback() {
      this.mediaQuery.addEventListener('change', this.debounceResizeHandler.bind(this), { passive: true });
      // Add listener for medium screen changes
      this.mediumQuery.addEventListener('change', this.debounceResizeHandler.bind(this), { passive: true });

      // Add click handler for more options
      this.setupMoreOptionsHandler();

      // Initial setup
      this.handleScreenChange(this.mediaQuery);
    }

    disonnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.debounceResizeHandler.bind(this));
      this.mediumQuery.removeEventListener('change', this.debounceResizeHandler.bind(this));
    }

    // Get the actual available swatch count from Liquid data or count visible labels
    getAvailableSwatchCount(container) {
      // First try to get count from Liquid-generated data
      const swatchCountScript = container.querySelector('script[data-swatch-count]');
      if (swatchCountScript) {
        try {
          return parseInt(swatchCountScript.textContent) || 0;
        } catch (e) {
          console.warn('Failed to parse swatch count from Liquid data:', e);
        }
      }

      // Fallback: count visible swatch labels (excluding more label)
      const availableSwatches = container.querySelectorAll('.opt-label:not(.color-opt-more)');
      return availableSwatches.length;
    }

    // Setup click handler for more options
    setupMoreOptionsHandler() {
      document.addEventListener('click', (event) => {
        const moreLabel = event.target.closest('.color-opt-more');
        if (!moreLabel) return;

        const targetId = moreLabel.getAttribute('for');
        if (!targetId) return;

        const targetInput = document.getElementById(targetId);
        if (!targetInput || !targetInput.dataset.variantId) return;

        const productLink = moreLabel.closest('.card').querySelector('.card-link');
        if (!productLink) return;

        const url = new URL(productLink.href);
        url.searchParams.set('variant', targetInput.dataset.variantId);
        window.location.href = url.toString();
      });
    }

    // Debounce function for resize event
    debounceResizeHandler(event) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(
        () => this.handleScreenChange(event),
        100
      );
    }

    // Calculate consistent size for all products
    calculateConsistentSize(container) {
      const containerWidth = container.offsetWidth;

      // Use maximum possible elements for this screen size
      let maxElements;
      if (this.mediaQuery.matches) {
        maxElements = this.mobileCount;
      } else if (this.mediumQuery.matches) {
        maxElements = this.mediumCount;
      } else {
        maxElements = this.desktopCount;
      }

      // Calculate space as if we always show maxElements
      const gapSpace = (maxElements - 1) * 6; // 6px gaps
      const safetyMargin = 10;
      const availableSpace = containerWidth - gapSpace - safetyMargin;
      const calculatedSize = Math.floor(availableSpace / maxElements);

      // Keep reasonable bounds
      return Math.max(24, Math.min(48, calculatedSize));
    }

    // Apply the consistent size
    applyConsistentSize(container) {
      const consistentSize = this.calculateConsistentSize(container);
      container.style.setProperty(
        "--consistent-swatch-size",
        consistentSize + "px"
      );
    }

    // Update all containers with consistent sizing
    updateAllContainerSizes() {
      this.swatchContainers.forEach((container) => {
        this.applyConsistentSize(container);
      });
    }

    // Handle screen change (resize or initial load) - minimal change to add medium screen support
    handleScreenChange(e) {
      let currentCount;

      // Determine which count to use based on screen size
      if (this.mediaQuery.matches) {
        // Mobile: max-width 1100px
        currentCount = this.mobileCount;
      } else if (this.mediumQuery.matches) {
        // Medium: 1100px - 1520px
        currentCount = this.mediumCount;
      } else {
        // Desktop: 1521px and above
        currentCount = this.desktopCount;
      }

      // Update all swatch containers
      this.swatchContainers.forEach(container => {
        this.updateSwatchContainer(container, currentCount);
      });
    }

    // Update individual swatch container
    updateSwatchContainer(container, currentCount) {
      // Get accurate count of available swatches
      const totalAvailableSwatches = this.getAvailableSwatchCount(container);

      // Get all swatch labels (excluding the +more label)
      const availableSwatches = container.querySelectorAll('.opt-label:not(.color-opt-more)');

      // Find existing "more" label
      let moreLabel = container.querySelector('.color-opt-more');

      // If we have more swatches than the current display count
      if (totalAvailableSwatches > currentCount) {
        const remainingCount = totalAvailableSwatches - currentCount;

        // Show only the first N swatches
        availableSwatches.forEach((swatch, index) => {
          if (index < currentCount) {
            swatch.style.display = '';
          } else {
            swatch.style.display = 'none';
          }
        });

        // Update or create the more label
        if (moreLabel) {
          // Update existing more label
          this.updateMoreLabel(moreLabel, remainingCount, container, currentCount);
          moreLabel.style.display = '';
        } else {
          // Create new more label if it doesn't exist
          this.createMoreLabel(container, remainingCount, currentCount);
        }
      } else {
        // Show all swatches if we have fewer than or equal to the limit
        availableSwatches.forEach(swatch => {
          swatch.style.display = '';
        });

        // Hide the more label if it exists
        if (moreLabel) {
          moreLabel.style.display = 'none';
        }
      }
    }

    // Update existing more label text and link
    updateMoreLabel(moreLabel, remainingCount, container, currentCount) {
      const countSpan = moreLabel.querySelector('span:not(.visually-hidden)');
      const hiddenSpan = moreLabel.querySelector('span.visually-hidden');

      if (countSpan) {
        countSpan.textContent = `+ ${remainingCount}`;
      }
      if (hiddenSpan) {
        hiddenSpan.textContent = `+ ${remainingCount}`;
      }

      // Update the 'for' attribute to point to the first hidden swatch
      const radioInputs = container.querySelectorAll('input[type="radio"]');
      const firstHiddenInput = radioInputs[currentCount]; // First hidden input (0-indexed)

      if (firstHiddenInput) {
        moreLabel.setAttribute('for', firstHiddenInput.id);
      }
    }

    // Create a new "more" label when needed
    createMoreLabel(container, remainingCount, currentCount) {
      // Find the FIRST HIDDEN radio input to link to (not the last visible one)
      const radioInputs = container.querySelectorAll('input[type="radio"]');
      const firstHiddenInput = radioInputs[currentCount]; // First hidden input (0-indexed)

      if (!firstHiddenInput) {
        return;
      }

      const moreLabel = document.createElement('label');
      moreLabel.className = 'link-hover-color color-opt-more cursor-pointer';
      moreLabel.setAttribute('for', firstHiddenInput.id);

      moreLabel.innerHTML = `
        <span>+ ${remainingCount}</span>
        <span class="visually-hidden js-value">+ ${remainingCount}</span>
      `;

      container.appendChild(moreLabel);
    }

    // Method to reinitialize when swatches are dynamically updated
    reinitialize() {
      this.swatchContainers = Array.from(document.querySelectorAll('.card__swatches'));
      this.handleScreenChange(this.mediaQuery);
    }

    // Debug method to log swatch information
    debug(containerId = null) {
      const containers = containerId
        ? [document.getElementById(containerId)]
        : this.swatchContainers;

      containers.forEach((container, index) => {
        if (!container) return;

        const totalCount = this.getAvailableSwatchCount(container);
        const visibleCount = container.querySelectorAll('.opt-label:not(.color-opt-more)').length;

        let currentLimit;
        if (this.mediaQuery.matches) {
          currentLimit = this.mobileCount;
        } else if (this.mediumQuery.matches) {
          currentLimit = this.mediumCount;
        } else {
          currentLimit = this.desktopCount;
        }

        console.log(`Swatch Container ${index + 1}:`, {
          totalAvailable: totalCount,
          visibleInDOM: visibleCount,
          currentLimit: currentLimit,
          shouldShowMore: totalCount > currentLimit
        });
      });
    }
  }
  customElements.define('swatch-manager', SwatchManager);
}